// eslint-disable-next-line max-classes-per-file
import { KeyboardEvent, useEffect } from "react";

type Handler = (event: KeyboardEvent) => boolean | void | Promise<boolean> | Promise<void>;

export class ScopedKeyboardEventHandler {
    private eventsKeyDown: Record<string, Handler[]> = Object.create(null);

    private eventsPresssed: Record<string, Handler[]> = Object.create(null);

    private eventsKeyUp: Record<string, Handler[]> = Object.create(null);

    public on(event: string, handler: Handler): void {
        this.eventsKeyDown[event] = this.eventsKeyDown[event] ?? [];
        this.eventsKeyDown[event].push(handler);
    }

    public onPressed(event: string, handler: Handler): void {
        this.eventsPresssed[event] = this.eventsPresssed[event] ?? [];
        this.eventsPresssed[event].push(handler);
    }

    public onKeyUp(event: string, handler: Handler): void {
        this.eventsKeyUp[event] = this.eventsKeyUp[event] ?? [];
        this.eventsKeyUp[event].push(handler);
    }

    public async emit(name: string, event: KeyboardEvent): Promise<void> {
        const list = (this.eventsKeyDown[name] ?? []).concat(this.eventsKeyDown[""]);
        await list?.forEachConcurrent(async handler => {
            handler?.(event);
        });
    }

    public async emitPressed(name: string, event: KeyboardEvent): Promise<void> {
        const list = (this.eventsPresssed[name] ?? []).concat(this.eventsPresssed[""]);
        await list?.forEachConcurrent(async handler => {
            handler?.(event);
        });
    }

    public async emitKeyUp(name: string, event: KeyboardEvent): Promise<void> {
        const list = (this.eventsKeyUp[name] ?? []).concat(this.eventsKeyUp[""]);
        await list?.forEachConcurrent(async handler => {
            handler?.(event);
        });
    }
}

export default class KeyboardEventHandler {
    private scopedHandlers: ScopedKeyboardEventHandler[] = [];

    public emit(name: string, event: KeyboardEvent): void {
        this.scopedHandlers.forEach(row => row.emit(name, event));
    }

    public emitPressed(name: string, event: KeyboardEvent): void {
        this.scopedHandlers.forEach(row => row.emitPressed(name, event));
    }

    public emitKeyUp(name: string, event: KeyboardEvent): void {
        this.scopedHandlers.forEach(row => row.emitKeyUp(name, event));
    }

    useShortcuts(handler: ((shortcuts: ScopedKeyboardEventHandler) => void), dependencies: unknown[]) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const shortcuts = new ScopedKeyboardEventHandler();
            this.scopedHandlers.push(shortcuts);
            handler(shortcuts);
            return () => {
                this.scopedHandlers = this.scopedHandlers.filter(row => row !== shortcuts);
            };
        }, dependencies);
    }
}
